import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk, createAppAsyncThunk } from "appThunk";
import {
    selectDirectCompetitorNames,
    selectPlaceCategories,
    selectStore,
    selectComparator,
    selectSizeBuckets
} from "modules/customer/insights/portfolioNew/portfolioSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { RootState } from "store";
import { median } from "mathjs";
import _ from "lodash";

import { ClassifiedCompetitor, Competitor, CompetitorClassification, loadCompetitors } from "./competitors";
import { selectFeatureFlags } from "modules/featureFlags/featureFlagsSlice";
import { CompetitorStoreCategory } from "./competitorStoreCategory";
import { SortDirection, stringSortExpression } from "utils/sortUtils";
import { ClassifiedComparatorCompetitor, ComparatorCompetitor, loadComparatorCompetitors } from "./comparatorCompetitors";
import { selectUserInfo } from "modules/auth/authSlice";
import { RagIndicator, RagIndicatorStatus } from "domain/ragIndicator";
import mathUtils from "utils/mathUtils";

interface CompetitionState {
    isLoading: boolean,
    hasErrors: boolean,
    competitorStoreCategories: CompetitorStoreCategory[],
    showAlternateCategoryCompetitors: boolean,
    competitors: Competitor[],
    comparatorCompetitors: ComparatorCompetitor[]
}

interface LoadCompetitionResponse {
    competitorStoreCategories: CompetitorStoreCategory[],
    competitors: Competitor[],
    comparatorCompetitors: ComparatorCompetitor[]
}

const initialState: CompetitionState = {
    isLoading: false,
    hasErrors: false,
    competitorStoreCategories: [],
    showAlternateCategoryCompetitors: false,
    competitors: [],
    comparatorCompetitors: []
};

const competitionSlice = createSlice({
    name: "customer/insights/portfolioNew/competitionNew",
    initialState,
    reducers: {
        clearCompetitors: (state) => {
            state.competitors = initialState.competitors;
        },
        clearComparatorCompetitors: (state) => {
            state.comparatorCompetitors = initialState.comparatorCompetitors;
        },
        clearCompetitorStoreCategories: (state) => {
            state.competitorStoreCategories = initialState.competitorStoreCategories;
        },
        clearShowAlternateCategoryCompetitors: (state) => {
            state.showAlternateCategoryCompetitors = initialState.showAlternateCategoryCompetitors;
        },
        chooseAllCompetitorStoreCategories: (state) => {
            state.competitorStoreCategories.forEach(category => category.setIsSelected(true));
        },
        deselectAllCompetitorStoreCategories: (state) => {
            state.competitorStoreCategories.forEach(category => category.setIsSelected(false));
        },
        toggleCompetitorStoreCategoryIsSelected: (state, action: PayloadAction<string>) => {
            const categoryName = action.payload;
            state.competitorStoreCategories.find(category => category.name === categoryName)?.toggleIsSelected();
        },
        toggleShowAlternateCategoryCompetitors: (state) => {
            state.showAlternateCategoryCompetitors = !state.showAlternateCategoryCompetitors;
        }
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadCompetitionNew.pending, (state: CompetitionState) => {
            state.isLoading = true;
            state.hasErrors = false;
            state.competitorStoreCategories = initialState.competitorStoreCategories;
            state.competitors = initialState.competitors;
            state.comparatorCompetitors = initialState.comparatorCompetitors;
        });
        builder.addCase(loadCompetitionNew.rejected, (state: CompetitionState) => {
            state.isLoading = false;
            state.hasErrors = true;
            state.competitorStoreCategories = initialState.competitorStoreCategories;
            state.competitors = initialState.competitors;
            state.comparatorCompetitors = initialState.comparatorCompetitors;
        });
        builder.addCase(loadCompetitionNew.fulfilled, (state: CompetitionState, action: PayloadAction<LoadCompetitionResponse>) => {
            state.isLoading = false;
            state.hasErrors = false;
            state.competitorStoreCategories = action.payload.competitorStoreCategories;
            state.competitors = action.payload.competitors;
            state.comparatorCompetitors = action.payload.comparatorCompetitors;
        });
    }
});

export const {
    chooseAllCompetitorStoreCategories,
    deselectAllCompetitorStoreCategories,
    toggleCompetitorStoreCategoryIsSelected,
    toggleShowAlternateCategoryCompetitors
} = competitionSlice.actions;

export const loadCompetitionNew = createAppAsyncThunk(
    "customer/insights/portfolioNew/competitionNew/loadCompetition",
    async (arg, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const accountId = selectUserInfo(state).accountId;
            const { enableCustomerInsightsPortfolioNearbyCompetitionNew: enableNearbyCompetionNew } = selectFeatureFlags(state);
            const selectedStore = selectStore(state);
            const comparator = selectComparator(state);
            const directCompetitorNames = selectDirectCompetitorNames(state);
            const placeCategories = selectPlaceCategories(state);

            const primaryPlaceCategory = placeCategories.find(item => item.id === selectedStore?.placeCategoryId);
            const secondaryPlaceCategories = placeCategories
                .filter(item => selectedStore?.secondaryPlaceCategoryIds.includes(item.id))
                .sort((a, b) => stringSortExpression(a.name, b.name, SortDirection.ASC));
            const primaryAndSecondaryCategories = primaryPlaceCategory ? [primaryPlaceCategory, ...secondaryPlaceCategories] : [];

            const allRelevantPlaceCategoryIds: number[] = primaryAndSecondaryCategories.map(category => category.id);
            allRelevantPlaceCategoryIds.push(...primaryAndSecondaryCategories.flatMap(category => category.indirectlyCompetingCategories));

            const competitorStoreCategories = primaryAndSecondaryCategories.map(category =>
                new CompetitorStoreCategory(category, category.id === selectedStore?.placeCategoryId, true)
            );

            const comparatorStores = comparator?.getStores() ?? [];

            const comparatorCompetitors = enableNearbyCompetionNew ? thunkAPI.dispatch(loadComparatorCompetitors(comparatorStores, directCompetitorNames, allRelevantPlaceCategoryIds, accountId)) : undefined;
            const competitorsPromise = enableNearbyCompetionNew ? thunkAPI.dispatch(loadCompetitors(selectedStore, directCompetitorNames, allRelevantPlaceCategoryIds, accountId)) : undefined;
            const results = await Promise.all([competitorsPromise, comparatorCompetitors]);

            const loadCompetitionResponse: LoadCompetitionResponse = {
                competitorStoreCategories: competitorStoreCategories,
                competitors: results[0] ?? [],
                comparatorCompetitors: results[1] ?? []
            };

            return loadCompetitionResponse;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading Competition New.", error));
            return thunkAPI.rejectWithValue([]);
        }
    }
);

export const clearCompetition = (): AppThunk => (dispatch) => {
    dispatch(competitionSlice.actions.clearCompetitorStoreCategories());
    dispatch(competitionSlice.actions.clearShowAlternateCategoryCompetitors());
    dispatch(competitionSlice.actions.clearCompetitors());
    dispatch(competitionSlice.actions.clearComparatorCompetitors());

};

export const selectIsLoading = (state: RootState) => {
    return state.customer.insights.portfolioNew.competitionNew.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.insights.portfolioNew.competitionNew.hasErrors;
};

export const selectCompetitors = (state: RootState) => {
    return state.customer.insights.portfolioNew.competitionNew.competitors;
};

export const selectComparatorCompetitors = (state: RootState) => {
    return state.customer.insights.portfolioNew.competitionNew.comparatorCompetitors;
};

export const selectCompetitorStoreCategories = (state: RootState) => {
    return state.customer.insights.portfolioNew.competitionNew.competitorStoreCategories;
};

export const selectShowAlternateCategoryCompetitors = (state: RootState) => {
    return state.customer.insights.portfolioNew.competitionNew.showAlternateCategoryCompetitors;
};

interface UniqueCompetitor {
    competitor: Competitor,
    id: string,
    storeName: string;
    brandName: string;
    primaryCategoryId: number;
    displayCategoryId: number;
    latitude: number;
    longitude: number;
    sizeBucketId: number;
    alternateCategoryIds: number[];
}

export const selectUniqueCompetitors = createSelector(
    selectCompetitors,
    (competitors) => {
        if (competitors.length === 0) {
            return [];
        }
        const uniqueCompetitors: UniqueCompetitor[] = _(competitors)
            .groupBy(competitor => competitor.id)
            .map(group => {
                const alternateCategoryIds = Array.from(new Set(group.flatMap(item => item.alternateCategoryIds)));
                return {
                    competitor: group.find(item => !item.isAlternateCategory) ?? group[0],
                    id: group[0].id,
                    storeName: group[0].storeName,
                    brandName: group[0].brandName,
                    primaryCategoryId: group[0].primaryCategoryId,
                    displayCategoryId: group[0].primaryCategoryId,
                    latitude: group[0].latitude,
                    longitude: group[0].longitude,
                    sizeBucketId: group[0].sizeBucketId,
                    alternateCategoryIds
                };
            })
            .value();
        return uniqueCompetitors;
    }
);

export const selectClassifiedCompetitors = createSelector(
    (state: RootState) => selectStore(state),
    (state: RootState) => selectDirectCompetitorNames(state),
    (state: RootState) => selectPlaceCategories(state),
    (state: RootState) => selectSizeBuckets(state),
    selectCompetitorStoreCategories,
    selectShowAlternateCategoryCompetitors,
    selectUniqueCompetitors,
    (
        selectedStore,
        directCompetitorNames,
        placeCategories,
        sizeBuckets,
        competitorStoreCategories,
        showAlternateCategoryCompetitors,
        uniqueCompetitors
    ) => {
        if (uniqueCompetitors.length === 0 || competitorStoreCategories.length === 0) {
            return [];
        }
        const selectedPlaceCategories = competitorStoreCategories.filter(category => category.isSelected());
        const selectedPlaceCategoryIds = selectedPlaceCategories.map(category => category.id);
        const selectedIndirectlyCompetingCategoryIds = selectedPlaceCategories.flatMap(category => category.indirectlyCompetingCategories);
        const allRelevantPlaceCategoryIds = [...selectedPlaceCategoryIds, ...selectedIndirectlyCompetingCategoryIds];
        const directCompetitorNamesLowerCase = directCompetitorNames.map(name => name.toLowerCase());

        const newCompetitors = uniqueCompetitors
            .filter(competitor =>
                directCompetitorNamesLowerCase.includes(competitor.storeName.toLowerCase())
                || directCompetitorNamesLowerCase.includes(competitor.brandName.toLowerCase())
                || allRelevantPlaceCategoryIds.includes(competitor.primaryCategoryId)
                || (showAlternateCategoryCompetitors && selectedPlaceCategoryIds.some(item => competitor.alternateCategoryIds.includes(item))))
            .map(competitor => {
                const classification = (directCompetitorNamesLowerCase.includes(competitor.storeName.toLowerCase())
                    || directCompetitorNamesLowerCase.includes(competitor.brandName.toLowerCase()))
                    ? CompetitorClassification.Direct
                    : selectedPlaceCategoryIds.includes(competitor.primaryCategoryId)
                        ? CompetitorClassification.Standard
                        : (showAlternateCategoryCompetitors && selectedPlaceCategoryIds.some(item => competitor.alternateCategoryIds.includes(item)))
                            ? CompetitorClassification.Alternate
                            : CompetitorClassification.Indirect;
                const distanceToStore = mathUtils.haversineDistance(
                    selectedStore?.latitude ?? 0,
                    selectedStore?.longitude ?? 0,
                    competitor?.latitude ?? 0,
                    competitor?.longitude ?? 0
                );
                const placeCategory = placeCategories.find(category => category.id === competitor.primaryCategoryId);
                const displayCategoryName = placeCategory?.name ?? "Unknown";
                const alternateCategoryNames = placeCategories
                    .filter(category => competitor.alternateCategoryIds.includes(category.id))
                    .map(category => category.name)
                    .join(', ');
                const sizeBucket = sizeBuckets.find(item => item.id === competitor.sizeBucketId);

                return new ClassifiedCompetitor(
                    competitor.competitor,
                    classification,
                    displayCategoryName,
                    alternateCategoryNames,
                    distanceToStore,
                    sizeBucket
                );
            });
        return newCompetitors;
    }
);

export const selectUniqueComparatorCompetitors = createSelector(
    selectComparatorCompetitors,
    (comparatorCompetitors) => {
        if (comparatorCompetitors.length === 0) {
            return [];
        }

        return _(comparatorCompetitors)
            .groupBy(competitor => competitor.comparatorStoreId)
            .map((group, comparatorStoreId) => {
                const newCompetitors = _(group)
                    .groupBy(competitor => competitor.competitorId)
                    .map(group => {
                        const alternateCategoryIds = Array.from(new Set(group.flatMap(item => item.alternateCategoryIds)));
                        return {
                            competitor: group.find(item => !item.isAlternateCategory) ?? group[0],
                            competitorId: group[0].competitorId,
                            storeName: group[0].storeName,
                            brandName: group[0].brandName,
                            primaryCategoryId: group[0].primaryCategoryId,
                            alternateCategoryIds
                        };
                    })
                    .value();
                return {
                    storeId: comparatorStoreId,
                    competitors: newCompetitors
                };
            })
            .value();
    }
);

export const selectClassifiedComparatorCompetitors = createSelector(
    (state: RootState) => selectDirectCompetitorNames(state),
    selectCompetitorStoreCategories,
    selectShowAlternateCategoryCompetitors,
    selectUniqueComparatorCompetitors,
    (directCompetitorNames, competitorStoreCategories, showAlternateCategoryCompetitors, uniqueComparatorCompetitors) => {
        if (competitorStoreCategories.length === 0) {
            return [];
        }

        const selectedPlaceCategories = competitorStoreCategories.filter(category => category.isSelected());
        const selectedPlaceCategoryIds = selectedPlaceCategories.map(category => category.id);
        const selectedIndirectlyCompetingCategoryIds = selectedPlaceCategories.flatMap(category => category.indirectlyCompetingCategories);
        const directCompetitorNamesLowerCase = directCompetitorNames.map(name => name.toLowerCase());

        return uniqueComparatorCompetitors.map(item => {
            const classifiedCompetitors: ClassifiedComparatorCompetitor[] = [];
            item.competitors.forEach(competitor => {
                if (directCompetitorNamesLowerCase.includes(competitor.storeName.toLowerCase())
                    || directCompetitorNamesLowerCase.includes(competitor.brandName.toLowerCase())) {
                    classifiedCompetitors.push(new ClassifiedComparatorCompetitor(
                        competitor.competitor,
                        CompetitorClassification.Direct,
                    ));
                } else if (selectedPlaceCategoryIds.includes(competitor.primaryCategoryId)) {
                    classifiedCompetitors.push(new ClassifiedComparatorCompetitor(
                        competitor.competitor,
                        CompetitorClassification.Standard,
                    ));
                } else if (showAlternateCategoryCompetitors
                    && selectedPlaceCategoryIds.some(item => competitor.alternateCategoryIds.includes(item))) {
                    classifiedCompetitors.push(new ClassifiedComparatorCompetitor(
                        competitor.competitor,
                        CompetitorClassification.Alternate,
                    ));
                } else if ((selectedIndirectlyCompetingCategoryIds.includes(competitor.primaryCategoryId))) {
                    classifiedCompetitors.push(new ClassifiedComparatorCompetitor(
                        competitor.competitor,
                        CompetitorClassification.Indirect,
                    ));
                }
            });
            return {
                storeId: item.storeId,
                competitors: classifiedCompetitors
            };
        });
    }
);

interface CompetitorCounts {
    directCompetitors: number,
    competitors: number,
    indirectCompetitors: number
}

export const selectClassifiedCompetitorCounts = createSelector(
    selectClassifiedCompetitors,
    selectClassifiedComparatorCompetitors,
    (selectedStoreCompetitors, comparatorCompetitors) => {

        const selectedStoreCount: CompetitorCounts = {
            directCompetitors: selectedStoreCompetitors.filter(store => store.classification === CompetitorClassification.Direct).length,
            competitors: selectedStoreCompetitors.filter(store => store.classification === CompetitorClassification.Standard || store.classification === CompetitorClassification.Alternate).length,
            indirectCompetitors: selectedStoreCompetitors.filter(store => store.classification === CompetitorClassification.Indirect).length,
        };

        const comparatorCounts: CompetitorCounts[] = comparatorCompetitors.map(item => {
            return {
                directCompetitors: item.competitors.filter(store => store.classification === CompetitorClassification.Direct).length,
                competitors: item.competitors.filter(store => store.classification === CompetitorClassification.Standard || store.classification === CompetitorClassification.Alternate).length,
                indirectCompetitors: item.competitors.filter(store => store.classification === CompetitorClassification.Indirect).length,
            };
        });

        const comparatorMedianCounts = comparatorCounts.length === 0 ? {
            directCompetitors: 0,
            competitors: 0,
            indirectCompetitors: 0
        } : {
            directCompetitors: median(comparatorCounts.map(item => item.directCompetitors)),
            competitors: median(comparatorCounts.map(item => item.competitors)),
            indirectCompetitors: median(comparatorCounts.map(item => item.indirectCompetitors))
        };


        return {
            selectedStore: selectedStoreCount,
            comparator: comparatorMedianCounts
        };
    }
);

export const selectSizeRelativeToDirectCompetitors = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectClassifiedCompetitors,
    (state: RootState) => selectStore(state),
    (state: RootState) => selectDirectCompetitorNames(state),
    (state: RootState) => selectSizeBuckets(state),
    (isLoading, hasErrors, competitors, selectedStore, directCompetitorNames, sizeBuckets) => {
        const id = "new-size-relative-to-direct-competitors";
        const label = "Size relative to direct competitors";

        const directCompetitors = competitors?.filter(store => store.classification === CompetitorClassification.Direct) ?? [];

        let ragStatus = RagIndicatorStatus.Info;
        let ragValue = "";

        if (isLoading || hasErrors) {
            return new RagIndicator(id, ragStatus, label, ragValue, isLoading, hasErrors);
        }

        if (directCompetitorNames.length === 0) {
            ragStatus = RagIndicatorStatus.NoData;
            ragValue = `
                This indicator isn't available because it requires you to set your company's direct competitors. 
                To evaluate this insight, someone with permission to upload data from your company will need to edit/upload the 
                Company dataset and refresh your company's Analytics.
            `;
            return new RagIndicator(id, ragStatus, label, ragValue, isLoading, hasErrors);
        }

        if (directCompetitors.length === 0) {
            ragStatus = RagIndicatorStatus.Green;
            ragValue = `No direct competitors within 10 minutes of your ${selectedStore?.name} store`;

            return new RagIndicator(id, ragStatus, label, ragValue, isLoading, hasErrors);
        }

        const competitorsWithSize = directCompetitors.map(store => {
            const sizeBucket = sizeBuckets.find(item => item.id === store.sizeBucketId);
            const size = sizeBucket?.inferrredSize ?? 0;
            return {
                ...store,
                size
            };
        });

        const selectedStoreSqft = selectedStore?.sizeInSquareFeet ?? 0;
        const numberOfLargerCompetitors = competitorsWithSize.filter(store => store.size > selectedStoreSqft).length;

        const percentageOfLargerCompetitors = 100 * numberOfLargerCompetitors / directCompetitors.length;

        if (percentageOfLargerCompetitors >= 75) {
            ragStatus = RagIndicatorStatus.Red;
            ragValue = `Most direct competitors within 10 minutes are larger than your ${selectedStore?.name} store`;
        } else if (percentageOfLargerCompetitors >= 25) {
            ragStatus = RagIndicatorStatus.Amber;
            ragValue = `Most direct competitors within 10 minutes are broadly the same size as your ${selectedStore?.name} store`;
        } else {
            ragStatus = RagIndicatorStatus.Green;
            ragValue = `Most direct competitors within 10 minutes are smaller than your ${selectedStore?.name} store`;
        }

        return new RagIndicator(id, ragStatus, label, ragValue, isLoading, hasErrors);
    }
);

export default competitionSlice;
